import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Search, Filter, X, Star, ChevronDown } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ExpertCard } from "@/components/ExpertCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { API } from "@/App";
import { useLocation } from "@/contexts/LocationContext";

export default function ExpertsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({ industries: [], expertise: [] });
  const { selectedCountry } = useLocation();
  
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    industry: searchParams.get("industry") || "",
    expertise: searchParams.get("expertise") || "",
    minRate: searchParams.get("minRate") || "",
    maxRate: searchParams.get("maxRate") || "",
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  // Re-fetch experts when filters OR selectedCountry changes
  useEffect(() => {
    fetchExperts();
  }, [filters, selectedCountry]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchExperts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.industry) params.append("industry", filters.industry);
      if (filters.expertise) params.append("expertise", filters.expertise);
      if (filters.minRate) params.append("min_rate", filters.minRate);
      if (filters.maxRate) params.append("max_rate", filters.maxRate);
      // Add country filter from LocationContext
      if (selectedCountry && selectedCountry !== "GLOBAL") {
        params.append("country", selectedCountry);
      }
      
      const response = await axios.get(`${API}/experts?${params.toString()}`);
      setExperts(response.data);
    } catch (error) {
      console.error("Error fetching experts:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      industry: "",
      expertise: "",
      minRate: "",
      maxRate: "",
    });
    setSearchParams({});
  };

  const activeFiltersCount = Object.values(filters).filter(v => v).length;

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-12 border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-4">{t('experts.directory')}</p>
            <h1 className="font-display text-4xl md:text-5xl mb-4">
              {t('experts.title')}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl">
              {t('experts.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-4 sm:py-8 bg-charcoal/30 border-b border-slate-800/50 sticky top-20 z-30 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col gap-4">
            {/* Search */}
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                placeholder={t('experts.search')}
                value={filters.search}
                onChange={(e) => updateFilter("search", e.target.value)}
                className="pl-12 bg-charcoal border-slate-800 focus:border-gold-500 rounded-sm h-12 w-full"
                data-testid="expert-search-input"
              />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 sm:gap-3">
              <Select value={filters.industry || "all"} onValueChange={(v) => updateFilter("industry", v === "all" ? "" : v)}>
                <SelectTrigger className="w-full lg:w-[180px] bg-charcoal border-slate-800 rounded-sm h-12 text-sm" data-testid="industry-filter">
                  <SelectValue placeholder={t('experts.filters.allIndustries')} />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-slate-800">
                  <SelectItem value="all">{t('experts.filters.allIndustries')}</SelectItem>
                  {categories.industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.expertise || "all"} onValueChange={(v) => updateFilter("expertise", v === "all" ? "" : v)}>
                <SelectTrigger className="w-full lg:w-[180px] bg-charcoal border-slate-800 rounded-sm h-12 text-sm" data-testid="expertise-filter">
                  <SelectValue placeholder={t('experts.filters.allExpertise')} />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-slate-800">
                  <SelectItem value="all">{t('experts.filters.allExpertise')}</SelectItem>
                  {categories.expertise.map((exp) => (
                    <SelectItem key={exp} value={exp}>{exp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.maxRate || "all"} onValueChange={(v) => updateFilter("maxRate", v === "all" ? "" : v)}>
                <SelectTrigger className="w-full lg:w-[180px] bg-charcoal border-slate-800 rounded-sm h-12 text-sm" data-testid="rate-filter">
                  <SelectValue placeholder={t('experts.filters.anyPrice')} />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-slate-800">
                  <SelectItem value="all">{t('experts.filters.anyPrice')}</SelectItem>
                  <SelectItem value="200">{t('experts.filters.under')} $200/hr</SelectItem>
                  <SelectItem value="400">{t('experts.filters.under')} $400/hr</SelectItem>
                  <SelectItem value="600">{t('experts.filters.under')} $600/hr</SelectItem>
                  <SelectItem value="1000">{t('experts.filters.under')} $1000/hr</SelectItem>
                </SelectContent>
              </Select>

              {activeFiltersCount > 0 && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-slate-400 hover:text-white col-span-2 sm:col-span-1"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('experts.filters.clear')} ({activeFiltersCount})
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {/* Results count */}
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <p className="text-slate-400 text-sm sm:text-base">
              {loading ? t('experts.results.loading') : `${experts.length} ${t('experts.results.found')}`}
            </p>
          </div>

          {/* Expert Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-charcoal border border-slate-800 rounded-sm h-64 sm:h-80 animate-pulse" />
              ))}
            </div>
          ) : experts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {experts.map((expert, index) => (
                <motion.div
                  key={expert.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ExpertCard expert={expert} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-slate-400 text-lg mb-4">{t('experts.results.noResults')}</p>
              <Button 
                variant="outline" 
                onClick={clearFilters}
                className="border-slate-700"
              >
                {t('experts.results.clearFilters')}
              </Button>
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
